import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Upload as UploadIcon,
  AccountBalance as PortfolioIcon,
  List as ListIcon,
  PieChart as HoldingsIcon,
  DeleteForever as DeleteForeverIcon,
  ExitToApp as ExitIcon,
  NotificationsActive as TriggerIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { portfolioService, Portfolio, CreatePortfolioData, Transaction, ExitedStock, ClosedPosition } from '../../services/portfolioService';
import TransactionDialog from './TransactionDialog';
import UploadDialog from './UploadDialog';
import OpenPositions from './OpenPositions';

const portfolioSchema = yup.object({
  name: yup.string().required('Portfolio name is required'),
  description: yup.string(),
});

const PortfolioComponent: React.FC = () => {
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<Portfolio | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'transactions' | 'holdings' | 'exited' | 'closed'>('holdings');
  const [exitedStocks, setExitedStocks] = useState<ExitedStock[]>([]);
  const [exitedLoading, setExitedLoading] = useState(false);
  const [creatingTriggers, setCreatingTriggers] = useState(false);
  const [closedPositions, setClosedPositions] = useState<ClosedPosition[]>([]);
  const [closedLoading, setClosedLoading] = useState(false);
  const [totalRealizedProfit, setTotalRealizedProfit] = useState(0);
  
  // Dialog states
  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState<Portfolio | null>(null);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPortfolioForMenu, setSelectedPortfolioForMenu] = useState<Portfolio | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreatePortfolioData>({
    resolver: yupResolver(portfolioSchema),
  });

  const loadPortfolios = useCallback(async () => {
    try {
      setLoading(true);
      const portfolios = await portfolioService.getPortfolios();
      setPortfolios(portfolios);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPortfolios();
  }, [loadPortfolios]);

  useEffect(() => {
    if (selectedPortfolio) {
      loadTransactions(selectedPortfolio.id);
    }
  }, [selectedPortfolio]);

  const loadTransactions = async (portfolioId: number) => {
    try {
      const data = await portfolioService.getTransactions(portfolioId);
      setTransactions(data);
    } catch (err: any) {
      toast.error('Failed to load transactions');
    }
  };

  const handleCreatePortfolio = async (data: CreatePortfolioData) => {
    try {
      if (editingPortfolio) {
        await portfolioService.updatePortfolio(editingPortfolio.id, data);
        toast.success('Portfolio updated successfully');
      } else {
        await portfolioService.createPortfolio(data);
        toast.success('Portfolio created successfully');
      }
      await loadPortfolios();
      handleClosePortfolioDialog();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save portfolio');
    }
  };

  const handleDeletePortfolio = async (portfolio: Portfolio) => {
    if (window.confirm(`Are you sure you want to delete "${portfolio.name}"?`)) {
      try {
        await portfolioService.deletePortfolio(portfolio.id);
        toast.success('Portfolio deleted successfully');
        await loadPortfolios();
        if (selectedPortfolio?.id === portfolio.id) {
          setSelectedPortfolio(portfolios.length > 1 ? portfolios[0] : null);
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Failed to delete portfolio');
      }
    }
    handleCloseMenu();
  };

  const handleClosePortfolioDialog = () => {
    setPortfolioDialogOpen(false);
    setEditingPortfolio(null);
    reset();
  };

  const handleEditPortfolio = (portfolio: Portfolio) => {
    setEditingPortfolio(portfolio);
    reset({
      name: portfolio.name,
      description: portfolio.description || '',
    });
    setPortfolioDialogOpen(true);
    handleCloseMenu();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, portfolio: Portfolio) => {
    setAnchorEl(event.currentTarget);
    setSelectedPortfolioForMenu(portfolio);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedPortfolioForMenu(null);
  };

  const handleClearAllTransactions = async () => {
    if (window.confirm('Are you sure you want to delete ALL transactions from ALL portfolios? This action cannot be undone!')) {
      try {
        const result = await portfolioService.clearAllTransactions();
        toast.success(result.message);
        await loadPortfolios(); // Refresh portfolios to update transaction counts
        if (selectedPortfolio) {
          await loadTransactions(selectedPortfolio.id); // Refresh transactions view
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Failed to clear transactions');
      }
    }
  };

  const loadExitedStocks = async () => {
    try {
      setExitedLoading(true);
      const response = await portfolioService.getExitedStocks();
      setExitedStocks(response.exited_stocks);
    } catch (err: any) {
      toast.error('Failed to load exited stocks');
    } finally {
      setExitedLoading(false);
    }
  };

  const handleCreateReentryTriggers = async () => {
    if (window.confirm('This will create price triggers for all exited stocks at 20% below their last sell price. Continue?')) {
      try {
        setCreatingTriggers(true);
        const result = await portfolioService.createReentryTriggers();
        toast.success(result.message);
        // Refresh exited stocks to show updated trigger status
        await loadExitedStocks();
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Failed to create re-entry triggers');
      } finally {
        setCreatingTriggers(false);
      }
    }
  };

  const loadClosedPositions = async () => {
    try {
      setClosedLoading(true);
      const response = await portfolioService.getClosedPositions();
      setClosedPositions(response.closed_positions);
      setTotalRealizedProfit(response.total_realized_profit);
    } catch (err: any) {
      toast.error('Failed to load closed positions');
    } finally {
      setClosedLoading(false);
    }
  };

  // Load exited stocks when tab changes to 'exited'
  useEffect(() => {
    if (activeTab === 'exited') {
      loadExitedStocks();
    } else if (activeTab === 'closed') {
      loadClosedPositions();
    }
  }, [activeTab]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const transactionColumns: GridColDef[] = [
    { field: 'stock_symbol', headerName: 'Symbol', width: 120 },
    { field: 'stock_name', headerName: 'Name', width: 200 },
    {
      field: 'transaction_type',
      headerName: 'Type',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'BUY' ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    { field: 'quantity', headerName: 'Quantity', width: 120, type: 'number' },
    {
      field: 'price',
      headerName: 'Price',
      width: 120,
      type: 'number',
      valueFormatter: (params) => formatCurrency(params.value as number),
    },
    {
      field: 'transaction_date',
      headerName: 'Date',
      width: 150,
      valueFormatter: (params) => new Date(params.value as string).toLocaleDateString(),
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          variant="outlined"
          size="small"
          color={params.value === 'MANUAL' ? 'primary' : 'default'}
        />
      ),
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="h5">Portfolios</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setPortfolioDialogOpen(true)}
          size="small"
          title="Create Portfolio"
        >
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={1}>
        {/* Compact Portfolio Selector */}
        <Grid item xs={12}>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            {portfolios.map((portfolio) => (
              <Tooltip key={portfolio.id} title={portfolio.description || ''} arrow>
                <Chip
                  icon={<PortfolioIcon />}
                  label={portfolio.name}
                  variant={selectedPortfolio?.id === portfolio.id ? 'filled' : 'outlined'}
                  color={selectedPortfolio?.id === portfolio.id ? 'primary' : 'default'}
                  onClick={() => setSelectedPortfolio(portfolio)}
                  onDelete={(e) => {
                    e.stopPropagation();
                    handleMenuOpen(e as any, portfolio);
                  }}
                  deleteIcon={<MoreVertIcon />}
                  sx={{ 
                    cursor: 'pointer',
                    height: 36,
                    '& .MuiChip-label': { fontWeight: selectedPortfolio?.id === portfolio.id ? 600 : 400 }
                  }}
                />
              </Tooltip>
            ))}
          </Box>
        </Grid>

        {/* Portfolio Content */}
        {selectedPortfolio && (
          <Grid item xs={12}>
            {/* Tab Navigation + Actions - Single Compact Row */}
            <Box display="flex" alignItems="center" mt={1} mb={1} gap={0.5}>
              <Button
                variant={activeTab === 'holdings' ? 'contained' : 'outlined'}
                startIcon={<HoldingsIcon />}
                onClick={() => setActiveTab('holdings')}
                size="small"
                sx={{ minWidth: 'auto', px: 1.5 }}
              >
                Open Positions
              </Button>
              <Button
                variant={activeTab === 'transactions' ? 'contained' : 'outlined'}
                startIcon={<ListIcon />}
                onClick={() => setActiveTab('transactions')}
                size="small"
                sx={{ minWidth: 'auto', px: 1.5 }}
              >
                All Transactions
              </Button>
              <Button
                variant={activeTab === 'exited' ? 'contained' : 'outlined'}
                startIcon={<ExitIcon />}
                onClick={() => setActiveTab('exited')}
                size="small"
                sx={{ minWidth: 'auto', px: 1.5 }}
                color="warning"
              >
                Exited (0-1 Qty)
              </Button>
              <Button
                variant={activeTab === 'closed' ? 'contained' : 'outlined'}
                startIcon={<DeleteForeverIcon />}
                onClick={() => setActiveTab('closed')}
                size="small"
                sx={{ minWidth: 'auto', px: 1.5 }}
                color="error"
              >
                Closed Positions
              </Button>
              
              {/* Action Buttons */}
              <Box ml="auto" display="flex" gap={0.5}>
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => setUploadDialogOpen(true)}
                  size="small"
                  sx={{ minWidth: 'auto', px: 1.5 }}
                  title="Upload File"
                >
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setTransactionDialogOpen(true)}
                  size="small"
                  sx={{ minWidth: 'auto', px: 1.5 }}
                  title="Add Transaction"
                >
                </Button>
              </Box>
            </Box>

            {/* Tab Content */}
            {activeTab === 'holdings' && (
              <OpenPositions 
                portfolioId={selectedPortfolio.id} 
                portfolioName={selectedPortfolio.name}
              />
            )}

            {activeTab === 'transactions' && (
              <Box>
                <Typography variant="subtitle1" mb={1.5} fontWeight="bold">
                  All Transactions - {selectedPortfolio.name}
                </Typography>
                <Box sx={{ height: 350, width: '100%' }}>
                  <DataGrid
                    rows={transactions}
                    columns={transactionColumns}
                    initialState={{
                      pagination: {
                        paginationModel: { page: 0, pageSize: 10 },
                      },
                    }}
                    pageSizeOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                  />
                </Box>
              </Box>
            )}

            {activeTab === 'exited' && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Exited Stocks (0-1 Qty) - Re-entry Candidates
                  </Typography>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={creatingTriggers ? <CircularProgress size={16} color="inherit" /> : <TriggerIcon />}
                    onClick={handleCreateReentryTriggers}
                    disabled={creatingTriggers || exitedStocks.length === 0}
                    size="small"
                  >
                    Create Re-entry Triggers (20% below)
                  </Button>
                </Box>
                
                {exitedLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : exitedStocks.length === 0 ? (
                  <Alert severity="info">
                    No exited stocks found. Stocks with 0 or 1 quantity remaining after sells will appear here.
                  </Alert>
                ) : (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                          <TableCell>Stock</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell align="right">Last Sell Price</TableCell>
                          <TableCell align="right">Last Sell Date</TableCell>
                          <TableCell align="right">Re-entry Trigger (20%↓)</TableCell>
                          <TableCell align="right">Current Price</TableCell>
                          <TableCell align="center">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {exitedStocks.map((stock) => (
                          <TableRow 
                            key={stock.symbol} 
                            hover
                            sx={{ 
                              backgroundColor: stock.below_reentry ? 'rgba(76, 175, 80, 0.1)' : 'inherit'
                            }}
                          >
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.25}>
                                <Link
                                  component="button"
                                  variant="body2"
                                  onClick={() => navigate(`/analysis?symbol=${encodeURIComponent(stock.symbol)}`)}
                                  sx={{ fontWeight: 'bold', textDecoration: 'none' }}
                                >
                                  {stock.symbol}
                                </Link>
                                <Tooltip title="Screener.in">
                                  <IconButton size="small" component="a" href={`https://www.screener.in/company/${stock.symbol.replace(/\.(NS|BO)$/, '')}/consolidated/`} target="_blank" rel="noopener" sx={{ p: 0 }}>
                                    <OpenInNewIcon sx={{ fontSize: '0.75rem', color: 'text.disabled' }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Typography variant="caption" display="block" color="textSecondary">
                                {stock.name}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{stock.quantity}</TableCell>
                            <TableCell align="right">₹{stock.last_sell_price?.toFixed(2)}</TableCell>
                            <TableCell align="right">
                              {stock.last_sell_date ? new Date(stock.last_sell_date).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                              ₹{stock.reentry_trigger_price?.toFixed(2)}
                            </TableCell>
                            <TableCell align="right">
                              {stock.current_price ? `₹${stock.current_price.toFixed(2)}` : '-'}
                            </TableCell>
                            <TableCell align="center">
                              {stock.below_reentry ? (
                                <Chip label="BUY SIGNAL" color="success" size="small" />
                              ) : stock.price_vs_reentry_pct !== null ? (
                                <Chip 
                                  label={`${stock.price_vs_reentry_pct > 0 ? '+' : ''}${stock.price_vs_reentry_pct.toFixed(1)}%`}
                                  color={stock.price_vs_reentry_pct < 10 ? 'warning' : 'default'}
                                  size="small"
                                  variant="outlined"
                                />
                              ) : (
                                <Chip label="No Price" size="small" variant="outlined" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Re-entry Triggers:</strong> Click the button above to create price triggers for all exited stocks. 
                    You'll receive a Telegram notification when any stock drops 20% below your last sell price - 
                    a potential opportunity to re-enter the position.
                  </Typography>
                </Alert>
              </Box>
            )}

            {activeTab === 'closed' && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Closed Positions (0 Qty) - Realized P/L
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Chip 
                      label={`Total Realized: ${totalRealizedProfit >= 0 ? '+' : ''}₹${totalRealizedProfit.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
                      color={totalRealizedProfit >= 0 ? 'success' : 'error'}
                      size="medium"
                    />
                    <Button
                      variant="contained"
                      color="warning"
                      startIcon={creatingTriggers ? <CircularProgress size={16} color="inherit" /> : <TriggerIcon />}
                      onClick={handleCreateReentryTriggers}
                      disabled={creatingTriggers || closedPositions.length === 0}
                      size="small"
                    >
                      Create Re-entry Triggers
                    </Button>
                  </Box>
                </Box>
                
                {closedLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : closedPositions.length === 0 ? (
                  <Alert severity="info">
                    No closed positions found. Stocks where you've sold all shares (quantity = 0) will appear here.
                  </Alert>
                ) : (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 'bold', fontSize: '0.75rem' } }}>
                          <TableCell>Stock</TableCell>
                          <TableCell align="right">Qty Traded</TableCell>
                          <TableCell align="right">Avg Buy</TableCell>
                          <TableCell align="right">Avg Sell</TableCell>
                          <TableCell align="right">Total Buy</TableCell>
                          <TableCell align="right">Total Sell</TableCell>
                          <TableCell align="right">Realized P/L</TableCell>
                          <TableCell align="right">Last Sell</TableCell>
                          <TableCell align="right">Current Price</TableCell>
                          <TableCell align="right">Re-entry (20%↓)</TableCell>
                          <TableCell align="center">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {closedPositions.map((position) => (
                          <TableRow 
                            key={position.symbol} 
                            hover
                            sx={{ 
                              backgroundColor: position.below_reentry ? 'rgba(76, 175, 80, 0.1)' : 'inherit',
                              '& td': { fontSize: '0.8rem' }
                            }}
                          >
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.25}>
                                <Link
                                  component="button"
                                  variant="body2"
                                  onClick={() => navigate(`/analysis?symbol=${encodeURIComponent(position.symbol)}`)}
                                  sx={{ fontWeight: 'bold', textDecoration: 'none' }}
                                >
                                  {position.symbol}
                                </Link>
                                <Tooltip title="Screener.in">
                                  <IconButton size="small" component="a" href={`https://www.screener.in/company/${position.symbol.replace(/\.(NS|BO)$/, '')}/consolidated/`} target="_blank" rel="noopener" sx={{ p: 0 }}>
                                    <OpenInNewIcon sx={{ fontSize: '0.75rem', color: 'text.disabled' }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Typography variant="caption" display="block" color="textSecondary">
                                {position.name}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{position.total_buy_quantity}</TableCell>
                            <TableCell align="right">₹{position.avg_buy_price?.toFixed(2)}</TableCell>
                            <TableCell align="right">₹{position.avg_sell_price?.toFixed(2)}</TableCell>
                            <TableCell align="right">₹{position.total_buy_value?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</TableCell>
                            <TableCell align="right">₹{position.total_sell_value?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</TableCell>
                            <TableCell 
                              align="right"
                              sx={{ 
                                fontWeight: 'bold',
                                color: position.realized_profit >= 0 ? 'success.main' : 'error.main'
                              }}
                            >
                              {position.realized_profit >= 0 ? '+' : ''}₹{position.realized_profit?.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              <Typography variant="caption" display="block" color="inherit">
                                ({position.realized_profit_pct >= 0 ? '+' : ''}{position.realized_profit_pct?.toFixed(1)}%)
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              ₹{position.last_sell_price?.toFixed(2)}
                              <Typography variant="caption" display="block" color="textSecondary">
                                {position.last_sell_date ? new Date(position.last_sell_date).toLocaleDateString() : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {position.current_price ? `₹${position.current_price.toFixed(2)}` : '-'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                              {position.reentry_trigger_price ? `₹${position.reentry_trigger_price.toFixed(2)}` : '-'}
                            </TableCell>
                            <TableCell align="center">
                              {position.below_reentry ? (
                                <Chip label="BUY SIGNAL" color="success" size="small" />
                              ) : position.price_vs_reentry_pct !== null ? (
                                <Chip 
                                  label={`${position.price_vs_reentry_pct > 0 ? '+' : ''}${position.price_vs_reentry_pct.toFixed(1)}%`}
                                  color={position.price_vs_reentry_pct < 10 ? 'warning' : 'default'}
                                  size="small"
                                  variant="outlined"
                                />
                              ) : (
                                <Chip label="No Price" size="small" variant="outlined" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Closed Positions:</strong> These are stocks where you've completely exited (sold all shares). 
                    The realized P/L shows your actual profit or loss from these trades. 
                    Use the re-entry trigger to get notified when prices drop 20% below your last sell price.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Grid>
        )}
      </Grid>

      {/* Portfolio Dialog */}
      <Dialog open={portfolioDialogOpen} onClose={handleClosePortfolioDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(handleCreatePortfolio)}>
          <DialogTitle>
            {editingPortfolio ? 'Edit Portfolio' : 'Create New Portfolio'}
          </DialogTitle>
          <DialogContent>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Portfolio Name"
              {...register('name')}
              error={!!errors.name}
              helperText={errors.name?.message}
              autoFocus
            />
            <TextField
              margin="normal"
              fullWidth
              label="Description"
              multiline
              rows={3}
              {...register('description')}
              error={!!errors.description}
              helperText={errors.description?.message}
            />

          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePortfolioDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingPortfolio ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Transaction Dialog */}
      {selectedPortfolio && (
        <TransactionDialog
          open={transactionDialogOpen}
          onClose={() => setTransactionDialogOpen(false)}
          portfolioId={selectedPortfolio.id}
          onSuccess={() => {
            loadTransactions(selectedPortfolio.id);
            setTransactionDialogOpen(false);
          }}
        />
      )}

      {/* Upload Dialog */}
      {selectedPortfolio && (
        <UploadDialog
          open={uploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          portfolioId={selectedPortfolio.id}
          onSuccess={() => {
            loadTransactions(selectedPortfolio.id);
            setUploadDialogOpen(false);
          }}
        />
      )}

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => selectedPortfolioForMenu && handleEditPortfolio(selectedPortfolioForMenu)}>
          Edit
        </MenuItem>
        <MenuItem onClick={() => selectedPortfolioForMenu && handleDeletePortfolio(selectedPortfolioForMenu)}>
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default PortfolioComponent; 